import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/forms/Form';
import { FormInput } from '@/components/forms/FormInput';
import { FormSelect } from '@/components/forms/FormSelect';
import { LabelWithHelp } from '@/components/forms/FieldHelp';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useCrudMutations } from '@/hooks/useCrudMutations';
import { AgentSelect } from '@/components/AgentSelect.js';
import { api } from '@/lib/api-client';
import { toast } from '@/components/ui/toast';
import { errorMessage } from '@/lib/errors';
import { SUPPORTED_CURRENCIES } from '@/lib/currencies';
import { bankLabel } from '@/lib/bank-label';
import { PASSWORD_MIN_LENGTH } from '@b1fr0st/contracts/common';
import { looksMasked } from '@b1fr0st/shared/pii/mask';

interface BankMaster {
  code: string;
  name: string;
  localName?: string | null;
  country?: string | null;
}

// Form-level schemas are intentionally lenient (no email-format gate, empty
// strings allowed) so partial input never blocks submit — svc-member is the
// authority on wire validation. Shapes mirror svc-member CreateBody / PatchBody.
const CreateSchema = z.object({
  agentId: z.string().uuid(),
  username: z.string().min(3).max(64),
  password: z.string().min(PASSWORD_MIN_LENGTH).max(128),
  phone: z.string().min(1).max(32),
  bankCode: z.string().min(1).max(16),
  accountNumber: z.string().min(3).max(32),
  accountName: z.string().min(1).max(255),
  firstName: z.string().max(128).optional(),
  lastName: z.string().max(128).optional(),
});

const UpdateSchema = z.object({
  phone: z.string().max(32).optional(),
  email: z.string().max(255).optional(),
  firstName: z.string().max(128).optional(),
  lastName: z.string().max(128).optional(),
  lineId: z.string().max(128).optional(),
  currency: z.string(),
});

/** Select value for `currency: null` — the player follows the brand site's display currency. */
const FOLLOW_SITE = 'site';

type CreateValues = z.infer<typeof CreateSchema>;
type UpdateValues = z.infer<typeof UpdateSchema>;

interface MemberRow {
  id: string;
  username: string;
  phone?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  lineId?: string | null;
  /** null = follows the site's display currency. */
  currency?: string | null;
  status: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing?: MemberRow | null;
}

const CREATE_DEFAULTS: CreateValues = {
  agentId: '',
  username: '',
  password: '',
  phone: '',
  bankCode: '',
  accountNumber: '',
  accountName: '',
  firstName: '',
  lastName: '',
};

/**
 * Member-table fields only — the bank triple is posted separately to
 * /members/:id/bank-accounts, so it must not leak into the member create body.
 */
function cleanCreate(v: CreateValues): Record<string, unknown> {
  const out: Record<string, unknown> = {
    agentId: v.agentId,
    username: v.username,
    password: v.password,
    phone: v.phone.trim(),
  };
  for (const k of ['firstName', 'lastName'] as const) {
    const val = v[k]?.trim();
    if (val) out[k] = val;
  }
  return out;
}

/** The contact fields svc-member may have handed us masked (ADR-0003). */
const CONTACT_FIELDS = ['phone', 'email', 'firstName', 'lastName', 'lineId'] as const;

/**
 * Send ONLY what the operator touched.
 *
 * This used to put all five contact fields in every body, seeded from the row
 * the drawer had read. A staff member without `member:reveal_pii` reads that
 * row MASKED — `081•••5678`, `s•••@gmail.com`, `so•••` — so pressing Save
 * without typing anything overwrote the player's real phone, email and LINE id
 * with a row of dots. svc-member refuses a mask outright now (MEMBER-429), but
 * a form that sends a field nobody edited is wrong on its own terms: it also
 * turned a concurrent edit by someone else into a silent revert.
 *
 * `dirty` is react-hook-form's `dirtyFields`, which compares against the values
 * `reset()` seeded — so a field the operator typed into and then restored is
 * correctly not sent, and a MASKED field (seeded blank, see `maskedFields`) is
 * sent only once a real value has been typed over it.
 *
 * Blank → null is kept: an operator who clears a field they can read means to
 * clear it, and the backend PatchBody is nullable.
 */
function cleanUpdate(
  v: UpdateValues,
  editing: MemberRow,
  dirty: Partial<Record<keyof UpdateValues, boolean>>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of CONTACT_FIELDS) {
    if (!dirty[k]) continue;
    const val = v[k]?.trim();
    out[k] = val ? val : null;
  }
  const currency = v.currency === FOLLOW_SITE ? null : v.currency;
  if (currency !== (editing.currency ?? null)) out.currency = currency;
  return out;
}

export function MemberForm({ open, onOpenChange, editing }: Props) {
  const { t, i18n } = useTranslation();
  const isEdit = !!editing;
  const { create, update } = useCrudMutations<MemberRow>('/api/v1/members', {
    labelKey: 'common.resources.member',
  });

  const banksQuery = useQuery<{ items: BankMaster[] }>({
    queryKey: ['banks-master'],
    queryFn: () => api.get('/api/v1/banks'),
    staleTime: 5 * 60 * 1000,
  });
  const bankOptions = React.useMemo(
    () => (banksQuery.data?.items ?? []).map((b) => ({ value: b.code, label: bankLabel(b, i18n.language) })),
    [banksQuery.data, i18n.language],
  );

  const createForm = useForm<CreateValues>({
    resolver: zodResolver(CreateSchema),
    defaultValues: CREATE_DEFAULTS,
  });

  const updateForm = useForm<UpdateValues>({
    resolver: zodResolver(UpdateSchema),
    defaultValues: { currency: FOLLOW_SITE },
  });

  const currencyOptions = React.useMemo(
    () => [
      { value: FOLLOW_SITE, label: t('members.followSite') },
      ...SUPPORTED_CURRENCIES.map((c) => ({ value: c.code, label: `${c.code} · ${c.name}` })),
    ],
    [t],
  );

  /**
   * Which loaded values are masks rather than values, and what to show instead.
   *
   * A masked field is seeded BLANK, not with the mask: seeding the mask is how
   * it got written back. The mask becomes the input's placeholder, so the
   * operator still sees the tail of the number they are confirming against,
   * and a hint under the field says that leaving it alone keeps it.
   */
  const maskedFields = React.useMemo(() => {
    const out: Partial<Record<(typeof CONTACT_FIELDS)[number], string>> = {};
    if (!editing) return out;
    for (const k of CONTACT_FIELDS) {
      const value = editing[k];
      if (looksMasked(value)) out[k] = value as string;
    }
    return out;
  }, [editing]);

  React.useEffect(() => {
    if (!open) return;
    if (isEdit && editing) {
      const seed = (k: (typeof CONTACT_FIELDS)[number]) =>
        maskedFields[k] ? '' : (editing[k] ?? '');
      updateForm.reset({
        phone: seed('phone'),
        email: seed('email'),
        firstName: seed('firstName'),
        lastName: seed('lastName'),
        lineId: seed('lineId'),
        currency: editing.currency ?? FOLLOW_SITE,
      });
    } else {
      createForm.reset(CREATE_DEFAULTS);
    }
  }, [open, isEdit, editing, maskedFields, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    const created = await create.mutateAsync(cleanCreate(values) as Partial<MemberRow>);
    try {
      await api.post(`/api/v1/members/${created.id}/bank-accounts`, {
        bankCode: values.bankCode,
        accountNumber: values.accountNumber.trim(),
        accountName: values.accountName.trim(),
        isDefault: true,
      });
    } catch (err) {
      // Member is already created; surface the bank error so the admin can retry via the drawer.
      toast.error(errorMessage(err, t('common.error')));
    }
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!editing) return;
    await update.mutateAsync({
      id: editing.id,
      data: cleanUpdate(values, editing, updateForm.formState.dirtyFields) as Partial<MemberRow>,
    });
    onOpenChange(false);
  });

  /** A masked field keeps its mask as the placeholder and explains itself. */
  const maskProps = (k: (typeof CONTACT_FIELDS)[number]) =>
    maskedFields[k]
      ? { placeholder: maskedFields[k], description: t('members.maskedFieldHint') }
      : {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('common.edit') : t('members.create')}</DialogTitle>
          <DialogDescription>
            {isEdit ? editing?.username : t('members.createHint')}
          </DialogDescription>
        </DialogHeader>
        {isEdit ? (
          <Form {...updateForm}>
            <form onSubmit={onUpdate} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <FormInput<UpdateValues> name="phone" label={<LabelWithHelp text={t('members.phone')} help={t('members.help.phone')} />} {...maskProps('phone')} />
                <FormInput<UpdateValues> name="email" label={<LabelWithHelp text={t('members.email')} help={t('members.help.email')} />} {...maskProps('email')} />
                <FormInput<UpdateValues> name="firstName" label={<LabelWithHelp text={t('members.firstName')} help={t('members.help.firstName')} />} {...maskProps('firstName')} />
                <FormInput<UpdateValues> name="lastName" label={<LabelWithHelp text={t('members.lastName')} help={t('members.help.lastName')} />} {...maskProps('lastName')} />
                <FormInput<UpdateValues> name="lineId" label={<LabelWithHelp text={t('members.lineId')} help={t('members.help.lineId')} />} {...maskProps('lineId')} />
                <FormSelect<UpdateValues>
                  name="currency"
                  label={<LabelWithHelp text={t('members.playerCurrency')} help={t('members.help.playerCurrency')} />}
                  options={currencyOptions}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={update.isPending}>
                  {t('common.save')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        ) : (
          <Form {...createForm}>
            <form onSubmit={onCreate} className="space-y-4">
              <FormField
                control={createForm.control}
                name="agentId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <LabelWithHelp text={t('members.agent')} help={t('members.help.agent')} />
                    </FormLabel>
                    <FormControl>
                      <AgentSelect
                        value={field.value}
                        onChange={field.onChange}
                        disabled={createForm.formState.isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-3">
                <FormInput<CreateValues> name="username" label={<LabelWithHelp text={t('auth.username')} help={t('members.help.username')} />} />
                <FormInput<CreateValues>
                  name="password"
                  type="password"
                  label={<LabelWithHelp text={t('auth.password')} help={t('members.help.password', { min: PASSWORD_MIN_LENGTH })} />}
                />
                <FormInput<CreateValues> name="phone" label={<LabelWithHelp text={t('members.phone')} help={t('members.help.phone')} />} />
                <FormSelect<CreateValues> name="bankCode" label={t('finance.bank')} options={bankOptions} />
                <FormInput<CreateValues> name="accountNumber" label={t('finance.accountNumber')} />
                <FormInput<CreateValues> name="accountName" label={t('finance.accountName')} />
                <FormInput<CreateValues> name="firstName" label={<LabelWithHelp text={t('members.firstName')} help={t('members.help.firstName')} />} />
                <FormInput<CreateValues> name="lastName" label={<LabelWithHelp text={t('members.lastName')} help={t('members.help.lastName')} />} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={create.isPending}>
                  {t('common.create')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export type { MemberRow };
